/**
 * @file Homepage Contact Form
 */

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

/**
 * Homepage Contact Form component
 */
const vHpContactForm = () => {
	const hpContactForm     = document.querySelector( '.v-hp-contact-form' );
	const hpContactFormWrap = document.querySelector( '.v-hp-contact-form__wrap' );

	if ( ! hpContactForm || ! hpContactFormWrap ) {
		return;
	}

	gsap.registerPlugin( ScrollTrigger );
	const mobile = matchMedia( '(max-width: 550px)' );

	document.addEventListener( 'vChartAnimated', () => {
		gsap.to( '.v-hp-contact-form__container', {
			opacity:       1,
			y:             0,
			duration:      .6,
			ease:          'sine.out',
			scrollTrigger: {
				trigger: '.v-hp-contact-form__wrap',
				start:   () => `${ mobile.matches ? 25 : 50 }% bottom`,
			},
		} );
	} );

	const input     = hpContactForm.querySelector( '.v-hp-contact-form__input' );
	const submit    = hpContactForm.querySelector( '.v-hp-contact-form__submit' );
	const introWrap = hpContactForm.querySelector( '.v-hp-contact-form__intro-wrap' );
	const intro     = introWrap.querySelector( '.v-hp-contact-form__intro' );
	const formWrap  = hpContactForm.querySelector( '.v-hp-contact-form__form-wrap' );
	const form      = formWrap.querySelector( '.v-hp-contact-form__form' );
	const close     = formWrap.querySelector( '.v-hp-contact-form__close' );

	let animating                   = false;
	introWrap.dataset.originalColor = getComputedStyle( introWrap ).backgroundColor;
	formWrap.dataset.originalColor  = getComputedStyle( formWrap ).backgroundColor;

	input.addEventListener( 'keyup', ( e ) => {
		if ( e.code === 'Enter' ) {
			submit.click();
		}
	} );

	submit.addEventListener( 'click', () => {
		if ( animating ) {
			return;
		}
		animating = true;

		submit.setAttribute( 'aria-expanded', 'true' );
		close.setAttribute( 'aria-expanded', 'true' );

		const emailField = form.querySelector( '[type="email"]' );
		if ( emailField ) {
			/**
			 * Creates new event
			 *
			 * @param {string} name event name
			 * @return {Event} event
			 */
			const keyupEvent = ( name ) => new Event( name, { bubbles: true } );

			emailField.value = input.value;
			emailField.dispatchEvent( keyupEvent( 'change' ) );
			emailField.dispatchEvent( keyupEvent( 'input' ) );
			emailField.dispatchEvent( keyupEvent( 'blur' ) );
		}

		formWrap.style.display = 'block';

		const introLayout = introWrap.getBoundingClientRect();
		const formLayout  = formWrap.getBoundingClientRect();

		const targetColor  = getComputedStyle( formWrap ).backgroundColor;
		const targetHeight = formLayout.height;
		const targetScaleX = formLayout.width / introLayout.width;

		formWrap.style.height = 0;

		gsap.set( intro, {
			opacity: 1,
			y:       0,
		} );

		gsap.set( introWrap, {
			scaleX:          1,
			height:          introLayout.height,
			backgroundColor: introWrap.dataset.originalColor,
		} );

		gsap.set( form, {
			opacity: 0,
			y:       10,
		} );

		const openAnimation = gsap.timeline( {
			defaults: {
				ease:     'sine.out',
				duration: .3,
			},
			onComplete() {
				hpContactForm.classList.add( 'v-hp-contact-form--open' );
				openAnimation
					.getChildren()
					.map( ( tween ) => [ ...tween.targets() ] )
					.flat()
					.forEach( ( target ) => target.removeAttribute( 'style' ) );
				animating = false;

				const firstField = form.querySelector( 'input' );
				if ( firstField ) {
					firstField.focus();
				}

				openAnimation.kill();
			},
		} )
			.to( intro, {
				opacity: 0,
				y:       -10,
			} )
			.addLabel( 'intro-faded' )
			.to( introWrap, {
				backgroundColor: targetColor,
				duration:        .6,
			}, 'intro-faded' )
			.to( introWrap, {
				scaleX:   targetScaleX,
				height:   targetHeight,
				duration: .6,
				onComplete() {
					introWrap.style.display = 'none';
					formWrap.removeAttribute( 'style' );
					formWrap.style.display  = 'block';
				},
			}, 'intro-faded' )
			.to( form, {
				opacity: 1,
				y:       0,
			} );
	} );

	close.addEventListener( 'click', () => {
		if ( animating ) {
			return;
		}
		animating = true;

		submit.setAttribute( 'aria-expanded', 'false' );
		close.setAttribute( 'aria-expanded', 'false' );

		const emailField = form.querySelector( '[type="email"]' );
		if ( emailField ) {
			emailField.value = input.value;
		}

		introWrap.style.display = 'block';

		const introLayout = introWrap.getBoundingClientRect();
		const formLayout  = formWrap.getBoundingClientRect();

		const targetColor  = introWrap.dataset.originalColor;
		const targetHeight = introLayout.height;
		const targetScaleX = introLayout.width / formLayout.width;

		introWrap.style.height = 0;

		gsap.set( form, {
			opacity: 1,
			y:       0,
		} );

		gsap.set( formWrap, {
			backgroundColor: formWrap.dataset.originalColor,
			height:          formLayout.height,
			scaleX:          1,
		} );

		const closeAnimation = gsap.timeline( {
			defaults: {
				ease:     'sine.out',
				duration: .3,
			},
			onComplete() {
				hpContactForm.classList.remove( 'v-hp-contact-form--open' );
				closeAnimation
					.getChildren()
					.map( ( tween ) => [ ...tween.targets() ] )
					.flat()
					.forEach( ( target ) => target.removeAttribute( 'style' ) );
				animating = false;
			},
		} )
			.to( form, {
				opacity: 0,
				y:       10,
			} )
			.addLabel( 'form-faded' )
			.to( formWrap, {
				backgroundColor: targetColor,
				duration:        .6,
			}, 'form-faded' )
			.to( formWrap, {
				scaleX:   targetScaleX,
				height:   targetHeight,
				duration: .6,
				onComplete() {
					formWrap.style.display  = 'none';
					introWrap.removeAttribute( 'style' );
					introWrap.style.display = 'block';
				},
			}, 'form-faded' )
			.to( intro, {
				opacity: 1,
				y:       0,
			} );
	} );
};

export default vHpContactForm;
